import { useCallback } from "react";

import type { ReservationLocalCancelFeeRejectInput } from "@/lib/gql/__generated__/graphql";

import { useCustomMutation } from "@/hooks/use-graphql-client";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { RESERVATION_LOCAL_CANCEL_FEE_TRANSFER_REJECT_FOR_MANAGEMENT_RESERVATION_ITEM_DETAIL_UPDATE_MUTATION } from "./schema";
import * as Shared from "../../../../_shared";

export const useReservationLocalCancelFeeTransferRejectUpdateMutation = () => {
	const { mutationFunc } = useCustomMutation(
		RESERVATION_LOCAL_CANCEL_FEE_TRANSFER_REJECT_FOR_MANAGEMENT_RESERVATION_ITEM_DETAIL_UPDATE_MUTATION,
	);
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const onUpdateReservationLocalCancelFeeTransferReject = useCallback(
		async (input: ReservationLocalCancelFeeRejectInput) => {
			showLoadingScreenCallback();
			return mutationFunc({
				variables: { input },
				update: (cache, { data }) => {
					/**
					 * キャッシュから予約詳細を取得
					 */
					const cached = cache.readQuery({
						query: Shared.Model.Repository.Query.RESERVATION_DETAIL_FOR_MANAGEMENT_RESERVATION_ITEM_QUERY,
						variables: { hotelID: input.hotelID, reservationId: input.reservationID },
					});
					/**
					 * レスポンスから更新されたステータスを取得
					 * @todo 現地決済のキャンセル料金の否認時に更新すべき値は実装後に再度検討する
					 */
					const updated = data?.reservationLocalCancelFeeTransferReject.reservationDetail;
					if (!cached || !updated) return;

					/**
					 * 予約更新のキャッシュを更新
					 */
					cache.writeQuery({
						query: Shared.Model.Repository.Query.RESERVATION_DETAIL_FOR_MANAGEMENT_RESERVATION_ITEM_QUERY,
						variables: { hotelID: input.hotelID, reservationId: input.reservationID },
						data: {
							reservationDetail: {
								...cached.reservationDetail,
								...updated,
							},
							repeaterReservationList: cached.repeaterReservationList,
						},
					});
				},
			})
				.then(({ errors }) => {
					if (!errors) {
						notifyUserEventInfo({
							variant: "default",
							title: "現地決済のキャンセル料金を非承認しました",
						});
					}
				})
				.finally(() => {
					hideLoadingScreenCallback();
				});
		},
		[hideLoadingScreenCallback, mutationFunc, showLoadingScreenCallback],
	);

	return {
		onUpdateReservationLocalCancelFeeTransferReject,
	};
};
